/**
 * @file components/audit-tasks/lab/OrganicMaterialReviewsCard.tsx
 * @description 有機資材審查內聯卡片：列表 Table + 「新增審查」Drawer。
 *
 * 範圍：
 * - 僅顯示本 task.caseId 對應案件下的資材審查記錄。
 * - 由 PhaseLab 控制 mount —— 僅在 task.caseScheme === 'TOP'（有機方案）下出現。
 *
 * 主要互動：
 * 1. 卡片頂部「新增審查」按鈕開啟右側 Drawer。
 * 2. Drawer 內表單欄位：
 *    - 資材（必填，從 /api/settings/materials 取，與 organic-materials 頁面一致）
 *    - 審查結果（必填，REVIEW_RESULTS 枚舉：approved / rejected / conditional）
 *    - 審查人（可選，最多 100 字）
 *    - 備注（可選，最多 2000 字）
 * 3. 提交呼叫 POST /api/cases/:caseId/organic-materials/review，成功後 invalidate 列表。
 *
 * 真實 schema 對齊（與 plan 不同）：
 * - GET endpoint 已 inner join materials 取出 materialName，前端直接顯示。
 * - reviewResult 用 REVIEW_RESULTS as const，避免硬編碼字串。
 */

import { useState } from 'react';
import { message } from '@/lib/antd-app';
import {
  Button,
  Card,
  Collapse,
  Drawer,
  Empty,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { REVIEW_RESULTS } from '@zhcb/shared';

import { apiClient } from '@/lib/api-client';
import { OrganicMaterialReferenceLinks } from '@/components/audit-tasks/lab/OrganicMaterialReferenceLinks';

/** 後端 GET /api/cases/:caseId/organic-materials/review 回傳的單筆結構（已 join materialName） */
interface OrganicMaterialReview {
  id: number;
  materialId: number;
  materialName: string;
  reviewResult: 'approved' | 'rejected' | 'conditional';
  reviewer: string | null;
  reviewedAt: string | null;
  notes: string | null;
  createdAt: string;
}

/** 資材下拉選項所需的最小 Material 結構（來自 /api/settings/materials） */
interface Material {
  id: number;
  name: string;
}

interface Props {
  /** 當前 audit task 對應的 case id；用於 query 過濾與 mutation URL */
  caseId: number;
}

/** Drawer 表單值 */
interface FormValues {
  materialId: number;
  reviewResult: 'approved' | 'rejected' | 'conditional';
  reviewer?: string;
  notes?: string;
}

/** 審查結果 → 顯示文字 */
const RESULT_LABEL: Record<OrganicMaterialReview['reviewResult'], string> = {
  approved: '通過',
  rejected: '駁回',
  conditional: '附條件通過',
};

/** 審查結果 → antd Tag 顏色 */
const RESULT_COLOR: Record<OrganicMaterialReview['reviewResult'], string> = {
  approved: 'success',
  rejected: 'error',
  conditional: 'warning',
};

/**
 * 有機資材審查內聯卡片。
 *
 * 用於 audit task 詳情頁 PhaseLab 區塊內，僅在有機方案（TOP）下 mount，
 * 內聯顯示本案件的資材審查列表並提供新增入口，避免使用者跳轉到獨立頁面。
 */
export function OrganicMaterialReviewsCard({ caseId }: Props) {
  const qc = useQueryClient();
  // Drawer 開關
  const [open, setOpen] = useState(false);
  // antd Form 實例
  const [form] = Form.useForm<FormValues>();

  // 列表查詢：本 case 的資材審查記錄
  const reviewsQ = useQuery<{ items: OrganicMaterialReview[] }>({
    queryKey: ['organic-material-reviews', caseId],
    queryFn: () =>
      apiClient.get<{ items: OrganicMaterialReview[] }>(
        `/api/cases/${caseId}/organic-materials/review`,
      ),
  });

  // 資材下拉選項：僅當 Drawer 開啟時拉取，避免無謂請求；與 organic-materials 頁面一致
  // 使用 pageSize=200 確保大多數情況下一次拉完。
  const materialsQ = useQuery<{ items: Material[] }>({
    queryKey: ['materials-for-omr'],
    queryFn: () => apiClient.get<{ items: Material[] }>('/api/settings/materials?pageSize=200'),
    enabled: open,
  });

  // 提交 mutation：POST 新建審查
  const submit = useMutation({
    mutationFn: async (values: FormValues) => {
      return apiClient.post(`/api/cases/${caseId}/organic-materials/review`, values);
    },
    onSuccess: () => {
      message.success('審查已建立');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['organic-material-reviews', caseId] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // Table 欄位定義
  const cols: ColumnsType<OrganicMaterialReview> = [
    {
      title: '資材名稱',
      dataIndex: 'materialName',
      ellipsis: true,
    },
    {
      title: '審查結果',
      dataIndex: 'reviewResult',
      width: 120,
      render: (r: OrganicMaterialReview['reviewResult']) => (
        <Tag color={RESULT_COLOR[r]}>{RESULT_LABEL[r]}</Tag>
      ),
    },
    {
      title: '審查人',
      dataIndex: 'reviewer',
      width: 100,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '審查時間',
      dataIndex: 'reviewedAt',
      width: 160,
      render: (d: string | null) => (d ? new Date(d).toLocaleString('zh-TW') : '—'),
    },
    {
      title: '備注',
      dataIndex: 'notes',
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      width: 160,
      render: (d: string) => new Date(d).toLocaleString('zh-TW'),
    },
  ];

  /** 關閉 Drawer 並重置表單 */
  const closeDrawer = () => {
    setOpen(false);
    form.resetFields();
  };

  return (
    <>
      <Card
        size="small"
        title="有機資材審查"
        extra={
          <Button
            type="primary"
            size="small"
            icon={<Plus size={14} />}
            onClick={() => setOpen(true)}
          >
            新增審查
          </Button>
        }
      >
        <Collapse
          size="small"
          style={{ marginBottom: 12 }}
          items={[
            {
              key: 'reference-links',
              label: '常用允用資訊查詢連結（可於 系統設置 → 業務字典 — 有機資材查詢連結 維護）',
              children: <OrganicMaterialReferenceLinks />,
            },
          ]}
        />
        {reviewsQ.isLoading ? (
          <Typography.Text>載入中…</Typography.Text>
        ) : reviewsQ.data?.items.length ? (
          <Table<OrganicMaterialReview>
            rowKey="id"
            dataSource={reviewsQ.data.items}
            columns={cols}
            pagination={false}
            size="small"
          />
        ) : (
          <Empty description="尚無資材審查" />
        )}
      </Card>
      <Drawer
        title="新增資材審查"
        open={open}
        onClose={closeDrawer}
        width={520}
        footer={
          <Space>
            <Button onClick={closeDrawer}>取消</Button>
            <Button type="primary" loading={submit.isPending} onClick={() => form.submit()}>
              提交
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={(v) => submit.mutate(v)}>
          <Form.Item name="materialId" label="資材" rules={[{ required: true }]}>
            <Select
              loading={materialsQ.isLoading}
              showSearch
              optionFilterProp="label"
              options={(materialsQ.data?.items ?? []).map((m) => ({
                value: m.id,
                label: m.name,
              }))}
              placeholder="選擇要審查的資材"
            />
          </Form.Item>
          <Form.Item name="reviewResult" label="審查結果" rules={[{ required: true }]}>
            <Select
              options={REVIEW_RESULTS.map((r) => ({
                value: r,
                label: RESULT_LABEL[r],
              }))}
              placeholder="選擇審查結果"
            />
          </Form.Item>
          <Form.Item name="reviewer" label="審查人">
            <Input maxLength={100} placeholder="可選" />
          </Form.Item>
          <Form.Item name="notes" label="備注">
            <Input.TextArea rows={4} maxLength={2000} showCount placeholder="可選" />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}
